import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { 
  ArrowLeft, 
  CheckCircle2,
  Leaf,
  Heart,
  AlertTriangle,
  Sparkles,
  FileText,
  MapPin,
  Clock,
  Video,
  ExternalLink,
  XCircle,
  Shield,
  Star,
  Brain,
  Flower2,
  Sun,
  Moon,
  Droplets
} from 'lucide-react';
import { toast } from 'sonner';

// Loading messages for the analysis phase
const ANALYSIS_MESSAGES = [
  { icon: Leaf, text: "Analyzing your Prakriti (constitution)...", color: "text-green-600" },
  { icon: Brain, text: "Evaluating dosha imbalances...", color: "text-purple-600" },
  { icon: Heart, text: "Reviewing your health concerns...", color: "text-red-500" },
  { icon: Flower2, text: "Matching herbal remedies...", color: "text-pink-500" },
  { icon: Sun, text: "Crafting lifestyle recommendations...", color: "text-amber-500" },
  { icon: Droplets, text: "Preparing dietary guidance...", color: "text-blue-500" },
  { icon: Moon, text: "Finalizing your personalized assessment...", color: "text-indigo-500" },
];

// Ayurvedic facts to display during loading
const AYURVEDA_FACTS = [
  "Ayurveda is over 5,000 years old, making it one of the world's oldest healing systems.",
  "The three doshas - Vata, Pitta, and Kapha - represent different combinations of the five elements.",
  "In Ayurveda, digestion is considered the cornerstone of good health.",
  "Your unique constitution (Prakriti) is determined at conception and remains constant throughout life.",
  "Ayurveda emphasizes prevention over cure through balanced lifestyle practices.",
  "The concept of 'Agni' (digestive fire) is central to Ayurvedic medicine.",
  "Ayurveda recognizes the connection between mind, body, and spirit in healing."
];

// Fancy Analysis Loading Screen Component
const AnalysisLoadingScreen = () => {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [currentFactIndex, setCurrentFactIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(90); // 90 seconds estimate
  const [completedSteps, setCompletedSteps] = useState([]);

  useEffect(() => {
    // Cycle through messages
    const messageInterval = setInterval(() => {
      setCurrentMessageIndex(prev => {
        const next = (prev + 1) % ANALYSIS_MESSAGES.length;
        if (next > 0) {
          setCompletedSteps(steps => [...new Set([...steps, prev])]);
        }
        return next;
      });
    }, 4000);

    // Cycle through facts
    const factInterval = setInterval(() => {
      setCurrentFactIndex(prev => (prev + 1) % AYURVEDA_FACTS.length);
    }, 8000);

    // Progress animation
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 95) return prev;
        return prev + Math.random() * 3;
      });
    }, 1000);

    // Countdown timer
    const countdownInterval = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) return 1;
        return prev - 1;
      });
    }, 1000);

    return () => {
      clearInterval(messageInterval);
      clearInterval(factInterval);
      clearInterval(progressInterval);
      clearInterval(countdownInterval);
    };
  }, []);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const CurrentIcon = ANALYSIS_MESSAGES[currentMessageIndex].icon;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[hsl(40,23%,96%)] to-[hsl(35,25%,92%)] flex items-center justify-center p-4">
      <div className="max-w-lg w-full">
        {/* Main Card */}
        <div className="bg-white rounded-3xl shadow-xl p-8 relative overflow-hidden">
          {/* Animated background pattern */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute top-0 left-0 w-40 h-40 bg-[hsl(22,70%,44%)] rounded-full -translate-x-1/2 -translate-y-1/2 animate-pulse"></div>
            <div className="absolute bottom-0 right-0 w-60 h-60 bg-[hsl(135,15%,33%)] rounded-full translate-x-1/2 translate-y-1/2 animate-pulse" style={{ animationDelay: '1s' }}></div>
          </div>

          {/* Content */}
          <div className="relative z-10">
            {/* Animated Icon */}
            <div className="flex justify-center mb-6">
              <div className="relative">
                <div className="w-24 h-24 bg-gradient-to-br from-[hsl(22,70%,44%)] to-[hsl(35,70%,50%)] rounded-full flex items-center justify-center animate-pulse">
                  <CurrentIcon className="w-12 h-12 text-white" />
                </div>
                {/* Rotating ring */}
                <div className="absolute inset-0 border-4 border-transparent border-t-[hsl(22,70%,44%)] rounded-full animate-spin" style={{ animationDuration: '2s' }}></div>
                <div className="absolute inset-[-4px] border-4 border-transparent border-b-[hsl(135,15%,33%)] rounded-full animate-spin" style={{ animationDuration: '3s', animationDirection: 'reverse' }}></div>
              </div>
            </div>

            {/* Title */}
            <h2 className="text-2xl font-bold text-center text-[hsl(120,10%,18%)] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
              Creating Your Assessment
            </h2>

            {/* Current Step */}
            <p className={`text-center font-medium mb-6 transition-all duration-500 ${ANALYSIS_MESSAGES[currentMessageIndex].color}`}>
              {ANALYSIS_MESSAGES[currentMessageIndex].text}
            </p>

            {/* Progress Steps */}
            <div className="flex justify-center gap-2 mb-6">
              {ANALYSIS_MESSAGES.map((_, index) => (
                <div
                  key={index}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    completedSteps.includes(index) 
                      ? 'bg-green-500' 
                      : index === currentMessageIndex 
                        ? 'bg-[hsl(22,70%,44%)] scale-125' 
                        : 'bg-stone-200'
                  }`}
                />
              ))}
            </div>

            {/* Progress Bar */}
            <div className="mb-4">
              <div className="flex justify-between text-sm text-stone-500 mb-2">
                <span>Progress</span>
                <span>{Math.round(progress)}%</span>
              </div>
              <div className="w-full bg-stone-100 rounded-full h-3 overflow-hidden">
                <div 
                  className="bg-gradient-to-r from-[hsl(22,70%,44%)] to-[hsl(35,70%,50%)] h-3 rounded-full transition-all duration-1000 relative"
                  style={{ width: `${progress}%` }}
                >
                  <div className="absolute inset-0 bg-white/30 animate-pulse"></div>
                </div>
              </div>
            </div>

            {/* Estimated Time */}
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-2 bg-stone-100 rounded-full px-4 py-2">
                <Clock className="w-4 h-4 text-stone-500" />
                <span className="text-sm text-stone-600">
                  Estimated time: <span className="font-mono font-semibold text-[hsl(22,70%,44%)]">{formatTime(timeRemaining)}</span>
                </span>
              </div>
            </div>

            {/* Divider */}
            <div className="border-t border-stone-200 pt-6">
              {/* Did You Know */}
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-4 h-4 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-amber-700 mb-1">Did you know?</p>
                    <p className="text-sm text-amber-800 leading-relaxed transition-all duration-500">
                      {AYURVEDA_FACTS[currentFactIndex]}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom message */}
        <p className="text-center text-stone-500 text-sm mt-6">
          Please don&apos;t close this page. Your personalized assessment is being prepared.
        </p>
      </div>
    </div>
  );
};


export const Diagnosis = () => {
  const navigate = useNavigate();
  const { id: consultationId } = useParams();
  const [searchParams] = useSearchParams();
  const [consultation, setConsultation] = useState(null);
  const [diagnosis, setDiagnosis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showBookingPrompt, setShowBookingPrompt] = useState(false);
  const [upgrading, setUpgrading] = useState(false);

  const handleUpgrade = async (newPlan) => {
    setUpgrading(true);
    try {
      const response = await api.post(`/api/consultations/${consultationId}/upgrade`, {
        new_plan: newPlan,
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Error initiating upgrade:', error);
      toast.error(error.response?.data?.detail || 'Failed to initiate upgrade');
      setUpgrading(false);
    }
  };

  useEffect(() => {
    fetchConsultation();
  }, [consultationId]);

  const fetchConsultation = async () => {
    try {
      const response = await api.get(`/api/consultations/${consultationId}`);
      setConsultation(response.data);
      
      // If already diagnosed, show the diagnosis
      if (response.data.diagnosis) {
        setDiagnosis({
          ayurvedic_diagnosis: response.data.diagnosis,
          modern_interpretation: response.data.modern_interpretation,
          recommendations: response.data.recommendations
        });
        
        // For consultation plan, show booking prompt if not already shown
        if (response.data.plan === 'consultation') {
          setShowBookingPrompt(true);
        }
      } else if (response.data.status === 'questions_completed') {
        // Generate diagnosis if questions completed but not diagnosed
        generateDiagnosis();
      } else if (response.data.payment_status !== 'paid') {
        toast.error('Payment required', { id: 'payment-required' });
        navigate(`/consultation/${consultationId}/payment`);
      }
    } catch (error) {
      console.error('Error fetching consultation:', error);
      toast.error('Failed to load consultation');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const generateDiagnosis = async () => {
    setGenerating(true);
    try {
      const response = await api.post(`/api/consultations/${consultationId}/diagnose`, {});
      setDiagnosis(response.data);
      
      // Refresh consultation to get updated status
      const updatedConsultation = await api.get(`/api/consultations/${consultationId}`);
      setConsultation(updatedConsultation.data);
      
      // Handle plan-specific messages
      if (response.data.plan === 'verified') {
        toast.success('Your report has been submitted for doctor verification. You will receive the verified report via email.', { id: 'diagnosis-verified' });
      } else if (response.data.plan === 'consultation') {
        setShowBookingPrompt(true);
        toast.success('Diagnosis generated! Please book your 1-on-1 consultation.', { id: 'diagnosis-consultation' });
      } else {
        toast.success('Diagnosis generated! A copy has been sent to your email.', { id: 'diagnosis-complete' });
      }
    } catch (error) {
      console.error('Error generating diagnosis:', error);
      toast.error('Failed to generate diagnosis');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[hsl(40,23%,96%)]">
        <div className="w-12 h-12 border-4 border-[hsl(22,70%,44%)] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (generating) {
    return <AnalysisLoadingScreen />;
  }

  // Show awaiting verification message for $29 plan
  if (consultation?.plan === 'verified' && consultation?.status === 'awaiting_verification') {
    return (
      <div className="min-h-screen bg-[hsl(40,23%,96%)]" data-testid="awaiting-verification-page">
        <header className="glass sticky top-0 z-50 border-b border-stone-200/50">
          <div className="max-w-4xl mx-auto px-4 md:px-8 py-4 flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/dashboard')}
              className="text-stone-600"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-semibold text-[hsl(120,10%,18%)]" style={{ fontFamily: 'Playfair Display, serif' }}>
                Awaiting Verification
              </h1>
            </div>
          </div>
        </header>

        <main className="max-w-2xl mx-auto px-4 md:px-8 py-12">
          <div className="consultation-card text-center">
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Clock className="w-10 h-10 text-blue-600" />
            </div>
            <h2 className="text-2xl font-bold text-[hsl(120,10%,18%)] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Your Report is Being Verified
            </h2>
            <p className="text-stone-600 mb-6 leading-relaxed">
              Thank you for choosing the <strong>Doctor-Reviewed Assessment</strong>. Your AI-generated assessment 
              has been sent to our certified Ayurvedic physician for review and validation.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6 text-left">
              <h4 className="font-semibold text-blue-800 mb-2">What happens next?</h4>
              <ul className="space-y-2 text-sm text-blue-700">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  Our doctor will review your concerns and AI assessment
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  They will add personalized notes and verify recommendations
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  You will receive the verified report via email within 24-48 hours
                </li>
              </ul>
            </div>
            <p className="text-sm text-stone-500 mb-6">
              Report will be sent to: <strong>{consultation?.client_email}</strong>
            </p>
            <Button
              onClick={() => navigate('/dashboard')}
              className="btn-primary"
            >
              Return to Dashboard
            </Button>
          </div>
        </main>
      </div>
    );
  }

  // Booking prompt for $59 consultation plan
  const BookingPromptModal = () => (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl max-w-lg w-full p-8 animate-slide-up">
        <div className="text-center">
          <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Video className="w-8 h-8 text-purple-600" />
          </div>
          <h2 className="text-2xl font-bold text-[hsl(120,10%,18%)] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            Book Your 1-on-1 Consultation
          </h2>
          <p className="text-stone-600 mb-6">
            Your AI assessment is ready! As part of your consultation package, you get a 45-minute 
            video call with our Ayurvedic physician. Book your appointment now.
          </p>
          <div className="space-y-3">
            <a
              href="https://tidycal.com/aiays/client-enquiry"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              <Video className="w-5 h-5" />
              Book Appointment Now
              <ExternalLink className="w-4 h-4" />
            </a>
            <Button
              variant="outline"
              onClick={() => setShowBookingPrompt(false)}
              className="w-full"
            >
              View My Report First
            </Button>
          </div>
          <p className="text-xs text-stone-500 mt-4">
            A copy of your report has been sent to your email
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[hsl(40,23%,96%)]" data-testid="diagnosis-page">
      {showBookingPrompt && consultation?.plan === 'consultation' && <BookingPromptModal />}
      
      {/* Header */}
      <header className="glass sticky top-0 z-50 border-b border-stone-200/50">
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/dashboard')}
              className="text-stone-600"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-semibold text-[hsl(120,10%,18%)]" style={{ fontFamily: 'Playfair Display, serif' }}>
                Your Ayurvedic Assessment
              </h1>
              <p className="text-sm text-stone-500">Customised Report</p>
            </div>
          </div>
          <div className="flex gap-3">
            {consultation?.plan === 'consultation' && (
              <a
                href="https://tidycal.com/aiays/client-enquiry"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary flex items-center gap-2"
                data-testid="book-consultation-btn"
              >
                <Video className="w-4 h-4" />
                Book Consultation
              </a>
            )}
            <Button
              variant="outline"
              onClick={() => navigate(`/consultation/${consultationId}/report`)}
              className="btn-secondary"
              data-testid="view-report-btn"
            >
              <FileText className="w-4 h-4 mr-2" />
              View Report
            </Button>
            <Button
              onClick={() => navigate('/panchkarma')}
              className="btn-primary"
              data-testid="book-panchkarma-btn"
            >
              <MapPin className="w-4 h-4 mr-2" />
              Book Panchkarma
            </Button>
          </div>
        </div>
      </header>

      {/* Progress */}
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-6">
        <div className="flex items-center gap-2 mb-8">
          {['Details', 'Concerns', 'Payment', 'Questions', 'Assessment'].map((step, index) => (
            <div key={step} className="flex items-center">
              <div className="step-indicator completed">
                <CheckCircle2 className="w-5 h-5" />
              </div>
              <span className="ml-2 text-sm text-[hsl(135,15%,33%)]">{step}</span>
              {index < 4 && <div className="w-6 h-px mx-3 bg-[hsl(135,15%,33%)]" />}
            </div>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 md:px-8 pb-12">
        {diagnosis ? (
          <div className="space-y-8">
            {/* Email notification banner */}
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center gap-3">
              <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              <div>
                <p className="font-medium text-emerald-800">Report sent to your email!</p>
                <p className="text-sm text-emerald-600">
                  A copy of this report has been sent to {consultation?.client_email}
                </p>
              </div>
            </div>

            {/* Ayurvedic Assessment */}
            <div className="card-elevated p-8" data-testid="ayurvedic-diagnosis">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-12 h-12 bg-[hsl(22,70%,44%)/0.1] rounded-xl flex items-center justify-center">
                  <Leaf className="w-6 h-6 text-[hsl(22,70%,44%)]" />
                </div>
                <h2 className="text-2xl font-bold text-[hsl(120,10%,18%)]" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Ayurvedic Assessment
                </h2>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                {diagnosis.ayurvedic_diagnosis && Object.entries(diagnosis.ayurvedic_diagnosis).map(([key, value]) => (
                  <div key={key} className="bg-stone-50 rounded-xl p-4">
                    <h4 className="text-sm font-medium text-stone-500 mb-1 capitalize">
                      {key.replace(/_/g, ' ')}
                    </h4>
                    <p className="text-[hsl(120,10%,18%)]">{value}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Modern Wellness Interpretation */}
            {diagnosis.modern_interpretation && (
              <div className="card-elevated p-8" data-testid="modern-interpretation">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <Heart className="w-6 h-6 text-blue-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-[hsl(120,10%,18%)]" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Modern Wellness Interpretation
                  </h2>
                </div>
                
                <div className="space-y-4">
                  {diagnosis.modern_interpretation.condition_overview && (
                    <p className="text-stone-700 leading-relaxed">
                      {diagnosis.modern_interpretation.condition_overview}
                    </p>
                  )}
                  {diagnosis.modern_interpretation.related_conditions?.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-4">
                      {diagnosis.modern_interpretation.related_conditions.map((condition, index) => (
                        <span key={index} className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm">
                          {condition}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Recommendations */}
            {diagnosis.recommendations && (
              <div className="grid md:grid-cols-2 gap-6">
                {/* Do's */}
                {diagnosis.recommendations.do_list?.length > 0 && (
                  <div className="card-elevated p-8" data-testid="recommendations-do">
                    <h3 className="text-xl font-bold text-emerald-700 mb-4 flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                      <CheckCircle2 className="w-6 h-6" />
                      Things to Follow
                    </h3>
                    <ul className="space-y-3">
                      {diagnosis.recommendations.do_list.map((item, index) => (
                        <li key={index} className="do-card">
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Don'ts */}
                {diagnosis.recommendations.avoid_list?.length > 0 && (
                  <div className="card-elevated p-8" data-testid="recommendations-avoid">
                    <h3 className="text-xl font-bold text-red-700 mb-4 flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                      <AlertTriangle className="w-6 h-6" />
                      Things to Avoid
                    </h3>
                    <ul className="space-y-3">
                      {diagnosis.recommendations.avoid_list.map((item, index) => (
                        <li key={index} className="avoid-card">
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Dietary Guidelines */}
            {diagnosis.recommendations?.dietary_guidelines?.length > 0 && (
              <div className="card-elevated p-8" data-testid="dietary-guidelines">
                <h3 className="text-xl font-bold text-[hsl(120,10%,18%)] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Dietary Guidelines
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {diagnosis.recommendations.dietary_guidelines.map((item, index) => (
                    <div key={index} className="flex items-start gap-3 bg-stone-50 rounded-lg p-4">
                      <Leaf className="w-5 h-5 text-[hsl(135,15%,33%)] mt-0.5" />
                      <span className="text-stone-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Herbal Remedies */}
            {diagnosis.recommendations?.herbal_remedies?.length > 0 && (
              <div className="card-elevated p-8" data-testid="herbal-remedies">
                <h3 className="text-xl font-bold text-[hsl(120,10%,18%)] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                  Herbal Remedies
                </h3>
                <div className="space-y-4">
                  {diagnosis.recommendations.herbal_remedies.map((remedy, index) => (
                    <div key={index} className="bg-stone-50 rounded-xl p-6">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold text-[hsl(120,10%,18%)]">{remedy.name}</h4>
                        <span className="text-sm text-[hsl(22,70%,44%)] font-medium mono">{remedy.dosage}</span>
                      </div>
                      <p className="text-stone-600 mb-3">{remedy.benefits}</p>
                      {remedy.warning && (
                        <div className="warning-box">
                          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                          <p className="text-sm text-amber-800">{remedy.warning}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Panchkarma Recommendation */}
            {diagnosis.recommendations?.panchkarma_recommendation?.recommended && (
              <div className="card-elevated p-8 border-2 border-[hsl(43,70%,52%)]" data-testid="panchkarma-recommendation">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-[hsl(43,70%,52%)/0.2] rounded-xl flex items-center justify-center">
                    <Sparkles className="w-6 h-6 text-[hsl(43,70%,52%)]" />
                  </div>
                  <h3 className="text-xl font-bold text-[hsl(120,10%,18%)]" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Panchkarma Recommended
                  </h3>
                </div>
                <p className="text-stone-600 mb-4">{diagnosis.recommendations.panchkarma_recommendation.reason}</p>
                {diagnosis.recommendations.panchkarma_recommendation.treatments?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-6">
                    {diagnosis.recommendations.panchkarma_recommendation.treatments.map((treatment, index) => (
                      <span key={index} className="badge-gold">{treatment}</span>
                    ))}
                  </div>
                )}
                <Button
                  onClick={() => navigate('/panchkarma', { state: { consultationId } })}
                  className="btn-primary"
                  data-testid="explore-centers-btn"
                >
                  <MapPin className="w-4 h-4 mr-2" />
                  Explore Panchkarma Centers
                </Button>
              </div>
            )}

            {/* AYUSH CARE 15 Section - Only for consultation plan */}
            {consultation?.plan === 'consultation' && (
              <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border-2 border-emerald-200 rounded-2xl p-6">
                <div className="text-center mb-4">
                  <span className="text-2xl">🌿</span>
                  <h3 className="text-xl font-bold text-emerald-800 mt-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                    AYUSH CARE 15
                  </h3>
                  <p className="text-emerald-700 font-medium">✨ 15-Day Complimentary Supportive Healing Plan ✨</p>
                </div>
                <div className="grid sm:grid-cols-2 gap-3 mb-4">
                  <div className="flex items-center gap-2 text-sm text-emerald-700">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    Personalised Diet Consultation
                  </div>
                  <div className="flex items-center gap-2 text-sm text-emerald-700">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    Holistic Health Evaluation
                  </div>
                  <div className="flex items-center gap-2 text-sm text-emerald-700">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    Accountability Buddy
                  </div>
                  <div className="flex items-center gap-2 text-sm text-emerald-700">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    Daily Follow-up on Call/Message
                  </div>
                  <div className="flex items-center gap-2 text-sm text-emerald-700 sm:col-span-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    Lifestyle & Diet Guidance for Lasting Health
                  </div>
                </div>
                <p className="text-sm text-emerald-600 text-center italic">
                  🤍 Offered as a privilege to our patients—so we can support, guide, and walk with you on your journey toward healing, because We Care.
                </p>
                <p className="text-xs text-emerald-500 text-center mt-2">
                  ➡️ Continued care plans available thereafter.
                </p>
              </div>
            )}

            {/* Disclaimer */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-amber-800 mb-2">Important Disclaimer</h4>
                  <p className="text-sm text-amber-700 leading-relaxed">
                    This assessment is generated by AI using Ayurvedic principles and user-provided information. 
                    It is for educational purposes only and should not be considered as medical advice. 
                    Our doctors are here for reviewing, validating, discussing, and guiding — not diagnosing diseases, 
                    prescribing medicines, or providing treatment. Please consult with a qualified healthcare professional 
                    or Ayurvedic practitioner to interpret, verify, and seek approval before following any recommendations. 
                    <strong> Ayurveda Consult takes no liability</strong> for any actions taken based on this report.
                  </p>
                </div>
              </div>
            </div>

            {/* Upgrade Options - Show only for basic plan */}
            {consultation?.plan === 'basic' && (
              <div className="border-t-2 border-[hsl(22,70%,44%)/0.3] pt-8">
                <div className="text-center mb-6">
                  <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-700 px-4 py-1.5 rounded-full text-sm font-semibold mb-3">
                    <Clock className="w-4 h-4" />
                    Limited Time Offer
                  </div>
                  <h3 className="text-xl font-semibold text-[hsl(120,10%,18%)]" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Enhance Your Consultation Experience
                  </h3>
                  <p className="text-stone-600 mt-2">Upgrade your plan and save 20%!</p>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  {/* Doctor-Reviewed Upgrade */}
                  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200 relative overflow-hidden">
                    <div className="absolute top-2 right-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full font-medium">
                      20% OFF
                    </div>
                    <div className="absolute top-2 left-2 bg-emerald-500 text-white text-xs px-2 py-1 rounded-full font-medium">
                      Safe
                    </div>
                    <div className="flex items-center gap-3 mb-3 mt-4">
                      <Shield className="w-8 h-8 text-blue-600" />
                      <div>
                        <h4 className="font-semibold text-[hsl(120,10%,18%)]">Doctor-Reviewed</h4>
                        <p className="text-sm text-stone-600">Expert review of your report</p>
                      </div>
                    </div>
                    <ul className="text-sm text-stone-600 space-y-1 mb-4">
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                        Validated by certified Ayurvedic doctor
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                        Additional personalized notes
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                        Priority email support
                      </li>
                    </ul>
                    <p className="text-xs text-amber-600 italic mb-3">Non-clinical review. Does not replace medical advice.</p>
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-stone-400 line-through text-sm">$10</span>
                        <span className="text-2xl font-bold text-blue-600 ml-2">$8</span>
                        <span className="text-sm text-stone-500"> USD</span>
                      </div>
                      <Button 
                        onClick={() => handleUpgrade('verified')}
                        disabled={upgrading}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                        data-testid="upgrade-verified-btn"
                      >
                        {upgrading ? 'Processing...' : 'Upgrade Now'}
                      </Button>
                    </div>
                  </div>

                  {/* 1-on-1 Consultation Upgrade */}
                  <div className="bg-gradient-to-br from-[hsl(22,70%,44%)/0.1] to-[hsl(43,70%,52%)/0.1] rounded-xl p-6 border-2 border-[hsl(22,70%,44%)/0.5] relative overflow-hidden">
                    <div className="absolute top-2 right-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full font-medium">
                      20% OFF
                    </div>
                    <div className="absolute top-2 left-2 bg-[hsl(22,70%,44%)] text-white text-xs px-2 py-1 rounded-full font-medium">
                      Value
                    </div>
                    <div className="flex items-center gap-3 mb-3 mt-4">
                      <Video className="w-8 h-8 text-[hsl(22,70%,44%)]" />
                      <div>
                        <h4 className="font-semibold text-[hsl(120,10%,18%)]">1-on-1 Consultation</h4>
                        <p className="text-sm text-stone-600">Personal video call with doctor</p>
                      </div>
                    </div>
                    <ul className="text-sm text-stone-600 space-y-1 mb-4">
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                        Everything in Doctor-Reviewed
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                        45-min personal video consultation
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                        Ask questions in real-time
                      </li>
                    </ul>
                    <p className="text-xs text-amber-600 italic mb-3">Ayurvedic diet and lifestyle guidance only.</p>
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-stone-400 line-through text-sm">$40</span>
                        <span className="text-2xl font-bold text-[hsl(22,70%,44%)] ml-2">$32</span>
                        <span className="text-sm text-stone-500"> USD</span>
                      </div>
                      <Button 
                        onClick={() => handleUpgrade('consultation')}
                        disabled={upgrading}
                        className="btn-primary"
                        data-testid="upgrade-consultation-btn"
                      >
                        {upgrading ? 'Processing...' : 'Upgrade Now'}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Show upgrade to consultation only for verified plan */}
            {consultation?.plan === 'verified' && consultation?.status !== 'awaiting_verification' && (
              <div className="border-t-2 border-[hsl(22,70%,44%)/0.3] pt-8">
                <div className="bg-gradient-to-br from-[hsl(22,70%,44%)/0.1] to-[hsl(43,70%,52%)/0.1] rounded-xl p-6 border-2 border-[hsl(22,70%,44%)/0.5] relative overflow-hidden">
                  <div className="absolute top-2 right-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full font-medium">
                    20% OFF
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <Video className="w-10 h-10 text-[hsl(22,70%,44%)]" />
                      <div>
                        <h4 className="font-semibold text-lg text-[hsl(120,10%,18%)]">Want to discuss your report personally?</h4>
                        <p className="text-stone-600">Upgrade to a 45-min video consultation with our Ayurvedic physician</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="mb-2">
                        <span className="text-stone-400 line-through text-sm">$30</span>
                        <span className="text-2xl font-bold text-[hsl(22,70%,44%)] ml-2">$24</span>
                        <span className="text-sm text-stone-500"> USD</span>
                      </div>
                      <Button 
                        onClick={() => handleUpgrade('consultation')}
                        disabled={upgrading}
                        className="btn-primary"
                        data-testid="upgrade-consultation-btn"
                      >
                        {upgrading ? 'Processing...' : 'Upgrade to 1-on-1'}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Feedback CTA */}
            {!consultation?.feedback_submitted && (
              <div className="bg-gradient-to-r from-[hsl(22,70%,44%)] to-[hsl(35,80%,45%)] rounded-xl p-6 text-white" data-testid="feedback-cta">
                <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                  <div>
                    <h3 className="text-xl font-semibold mb-1">How was your experience?</h3>
                    <p className="text-white/80 text-sm">
                      Share your feedback and receive a free Ayurveda eBook as our thank you gift!
                    </p>
                  </div>
                  <Button
                    onClick={() => navigate(`/feedback/${consultationId}`)}
                    className="bg-white text-[hsl(22,70%,44%)] hover:bg-white/90 font-semibold px-6"
                    data-testid="give-feedback-btn"
                  >
                    Give Feedback & Get eBook
                  </Button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-stone-600">No assessment available yet.</p>
            <Button onClick={generateDiagnosis} className="btn-primary mt-4">
              Generate Assessment
            </Button>
          </div>
        )}
      </main>
    </div>
  );
};
